// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardProvider, useWizard } from '../components/wizard/WizardContext';
import { WizardProgress } from '../components/wizard/WizardProgress';
import { WizardCompletionScreen } from '../components/wizard/completion';
import { CreateRelationshipStep } from '../components/wizard/steps/CreateRelationshipStep';
import { AddPoliciesStep } from '../components/wizard/steps/AddPoliciesStep';
import { advisorApi } from '../api/advisorApi';
import { invitationApi } from '../api/invitationApi';
import { featureFlags } from '../config/featureFlags';
import type { BatchSendInvitationsRequest } from '../types/invitation';

/**
 * Extract brokerageId from the current hostname subdomain
 */
const extractBrokerageIdFromHostname = (): string => {
  const hostname = globalThis.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'tb1';
  }

  const parts = hostname.split('.');
  if (parts.length > 0) {
    return parts[0];
  }

  return 'tb1';
};

function WizardContent() {
  const navigate = useNavigate();
  const {
    state,
    setSendingState,
  } = useWizard();

  // Handle skip invitations - navigate to relationship detail page
  const handleSkipInvitations = useCallback(() => {
    if (state.completionState) {
      navigate(`/relationships/${state.completionState.profileId}`);
    }
  }, [navigate, state.completionState]);

  // Handle send invitations
  const handleSendInvitations = useCallback(async () => {
    if (!state.completionState) return;

    const { profileId, createdClients, customSubject, customBody, isEmailModified } = state.completionState;

    setSendingState('sending');

    try {
      // Invitation-only send: the single-step wizard registers no policies,
      // so there is no LOA to prepare here. LOA Profile Requests are created
      // by the policy flows (relationship detail page / legacy step 2's
      // send-invitations page) once policies exist.
      const request: BatchSendInvitationsRequest = {
        clients: createdClients.map(client => ({
          clientId: client.clientId,
          ...(isEmailModified && customSubject ? { customSubject } : {}),
          ...(isEmailModified && customBody ? { customBody } : {}),
        })),
      };

      const response = await invitationApi.sendInvitationsBatch(profileId, request);

      if (response.failed > 0) {
        const failedNames = response.results
          .filter(r => !r.success)
          .map(r => r.clientName)
          .join(', ');
        setSendingState('error', `Failed to send to: ${failedNames}`);
      } else {
        setSendingState('success');
        navigate(`/relationships/${profileId}`);
      }
    } catch (err: unknown) {
      console.error('Failed to send invitations:', err);
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to send invitations. Please try again.';
      setSendingState('error', errorMessage);
    }
  }, [navigate, state.completionState, setSendingState]);

  // Render current step
  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case 1:
        return <CreateRelationshipStep />;
      case 2:
        return <AddPoliciesStep />;
      default:
        return <CreateRelationshipStep />;
    }
  };

  // Show completion screen if profile was created successfully
  if (state.completionState) {
    return (
      <WizardCompletionScreen
        onSkipInvitations={handleSkipInvitations}
        onSendInvitations={handleSendInvitations}
      />
    );
  }

  return (
    <>
      <WizardProgress />
      <div className="mt-2 flex-1 flex flex-col">
        {renderCurrentStep()}
      </div>
    </>
  );
}

export function RelationshipWizardPage() {
  const [brokerageId] = useState<string>(extractBrokerageIdFromHostname());
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [shouldLoadDraft, setShouldLoadDraft] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  // Whether this brokerage still has the legacy "Add Policies" step (step 2).
  // null while the per-brokerage flag is loading; defaults to false (the
  // platform default: single-step wizard) if the lookup fails.
  const [policiesStepEnabled, setPoliciesStepEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    advisorApi.setBrokerageId(brokerageId);

    let cancelled = false;
    advisorApi.getUiFeatures()
      .then(features => {
        if (!cancelled) setPoliciesStepEnabled(features.policiesWizardStep === true);
      })
      .catch(err => {
        console.warn('Failed to load advisor UI features; defaulting to single-step wizard', err);
        if (!cancelled) setPoliciesStepEnabled(false);
      });

    if (!featureFlags.enableWizardSaveDraft) {
      localStorage.removeItem('loa_relationship_wizard_draft');
      setShouldLoadDraft(false);
      setLoading(false);
      return () => { cancelled = true; };
    }

    try {
      const draftJson = localStorage.getItem('loa_relationship_wizard_draft');
      if (draftJson) {
        const draft = JSON.parse(draftJson);
        const isExpired = Date.now() - draft.timestamp > 24 * 60 * 60 * 1000;
        const isDifferentBrokerage = draft.brokerageId !== brokerageId;

        if (!isExpired && !isDifferentBrokerage) {
          setShowDraftModal(true);
          setLoading(false);
          return () => { cancelled = true; };
        }
      }
    } catch {
      // Ignore parsing errors
    }

    setShouldLoadDraft(false);
    setLoading(false);
    return () => { cancelled = true; };
  }, [brokerageId]);

  const handleLoadDraft = () => {
    setShowDraftModal(false);
    setShouldLoadDraft(true);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem('loa_relationship_wizard_draft');
    setShowDraftModal(false);
    setShouldLoadDraft(false);
  };

  if (loading || policiesStepEnabled === null) {
    return (
      <div className="w-full mt-12 px-6">
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-grey-02">Loading...</span>
        </div>
      </div>
    );
  }

  // Show modal if draft exists and user hasn't decided yet
  if (showDraftModal && shouldLoadDraft === null) {
    return (
      <div className="w-full py-8 px-6">

        {/* Draft Recovery Modal */}
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-card shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-black mb-2">Resume Previous Work?</h3>
            <p className="text-sm text-grey-02 mb-6">
              You have an unsaved draft from a previous session. Would you like to
              continue where you left off, or start fresh?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="h-[40px] px-6 border border-[#E1E1E1] text-[#1E1E1E] rounded-button text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Start Fresh
              </button>
              <button
                type="button"
                onClick={handleLoadDraft}
                className="h-[40px] px-6 bg-purple-primary text-white rounded-button text-sm font-medium hover:bg-[#220852] transition-colors"
              >
                Continue Draft
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <WizardProvider brokerageId={brokerageId} policiesStepEnabled={policiesStepEnabled}>
        <DraftLoaderWrapper shouldLoadDraft={shouldLoadDraft === true}>
          <WizardContent />
        </DraftLoaderWrapper>
      </WizardProvider>
    </div>
  );
}

function DraftLoaderWrapper({
  children,
  shouldLoadDraft
}: Readonly<{
  children: React.ReactNode;
  shouldLoadDraft: boolean;
}>) {
  const { loadDraft } = useWizard();

  useEffect(() => {
    if (shouldLoadDraft) {
      loadDraft();
    }
  }, [shouldLoadDraft, loadDraft]);

  return <>{children}</>;
}
